<template>
  <v-dialog
    :model-value="modelValue"
    max-width="723"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <div class="dialog-header">
        <span class="dialog-title">Search Chat</span>
        <v-btn
          icon
          size="small"
          variant="text"
          aria-label="Close search dialog"
          @click="$emit('update:modelValue', false)"
        >
          <v-icon :size="40" aria-hidden="true"
            >mdi-close-circle-outline</v-icon
          >
        </v-btn>
      </div>

      <div class="dialog-body">
        <!-- Search Input -->
        <div class="search-bar">
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search Here"
            @input="handleSearch"
          />
          <v-icon :size="20" class="search-icon" aria-hidden="true"
            >mdi-magnify</v-icon
          >
        </div>

        <!-- New Chat Expandable -->
        <div
          class="new-chat-selector"
          v-click-outside="handleOutsideClick"
          @click="expanded = !expanded"
        >
          <div class="new-chat-btn">
            <v-icon :size="24" class="new-chat-icon" aria-hidden="true"
              >mdi-tooltip-edit-outline</v-icon
            >
            <span class="new-chat-text">New Chat</span>
            <v-icon :size="24" class="new-chat-chevron" aria-hidden="true">
              {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </div>

          <v-expand-transition>
            <div v-show="expanded">
              <v-list density="compact" class="pa-0 agent-list">
                <v-list-item
                  v-for="agent in agents"
                  :key="agent.name"
                  class="agent-item"
                  @click.stop="handleNewChat(agent)"
                >
                  <v-list-item-title class="agent-item-title">
                    {{ agent.name }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </div>
          </v-expand-transition>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center mt-4">
          <v-progress-circular
            indeterminate
            size="24"
            width="2"
          ></v-progress-circular>
        </div>

        <!-- Search Results / Conversation History -->
        <div v-else class="conversation-groups">
          <div
            v-for="group in groupedConversations"
            :key="group.label"
            class="conversation-group"
          >
            <div class="group-label">{{ group.label }}</div>
            <div class="group-items">
              <div
                v-for="conversation in group.conversations"
                :key="conversation.id"
                :class="[
                  'history-item',
                  {
                    'history-item--active':
                      route.name === 'conversation' &&
                      route.params.id === conversation.id,
                  },
                ]"
                @click="handleSelectConversation(conversation)"
              >
                <span class="history-item-title">
                  {{ conversation.title || conversation.id }}
                </span>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-if="groupedConversations.length === 0 && searchQuery.trim()"
            class="empty-state"
          >
            No conversations found
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { Agent } from '@/services/agentService';
import {
  type Conversation,
  fetchConversations,
  searchConversations,
} from '@/services/conversationService';
import { useConversationStore } from '@/stores/conversationStore';
import { PaginatedCache } from '@/utils/cache/PaginatedCache';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const dialogCache = new PaginatedCache<Conversation>({
  ttl: 5 * 60 * 1000,
  storageKeyPrefix: 'search-dialog-conversations',
});
const CACHE_KEY = dialogCache.generateKey({ limit: 100, offset: 0 });

interface ConversationGroup {
  label: string;
  conversations: Conversation[];
}

const props = defineProps<{
  modelValue: boolean;
  agents: Agent[];
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'select-conversation': [conversation: Conversation];
  'new-chat': [agent: Agent];
}>();

const route = useRoute();
const conversationStore = useConversationStore();

const searchQuery = ref('');
const searchResults = ref<Conversation[]>([]);
const allConversations = ref<Conversation[]>([]);
const isLoading = ref(false);
const expanded = ref(false);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const conversations = computed(() => {
  if (searchQuery.value.trim()) {
    return searchResults.value;
  }
  return allConversations.value;
});

const loadAllConversations = async () => {
  try {
    const cached = await dialogCache.get(CACHE_KEY);
    if (cached) {
      allConversations.value = cached.data;
      return;
    }

    isLoading.value = true;
    const response = await fetchConversations(100, 0);
    allConversations.value = response.data;
    await dialogCache.set(CACHE_KEY, {
      data: response.data,
      total: response.total,
      hasMore: response.has_more,
      timestamp: Date.now(),
    });
  } catch {
    allConversations.value = [];
  } finally {
    isLoading.value = false;
  }
};

const groupedConversations = computed((): ConversationGroup[] => {
  const groups: ConversationGroup[] = [];
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(todayStart);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const today: Conversation[] = [];
  const previous7Days: Conversation[] = [];
  const older: Conversation[] = [];

  for (const conv of conversations.value) {
    const convDate = new Date(conv.update_time || conv.create_time);
    if (convDate >= todayStart) {
      today.push(conv);
    } else if (convDate >= sevenDaysAgo) {
      previous7Days.push(conv);
    } else {
      older.push(conv);
    }
  }

  if (today.length > 0) {
    groups.push({ label: 'Today', conversations: today });
  }
  if (previous7Days.length > 0) {
    groups.push({ label: 'Previous 7 Days', conversations: previous7Days });
  }
  if (older.length > 0) {
    groups.push({ label: 'Older', conversations: older });
  }

  return groups;
});

const handleSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  const query = searchQuery.value.trim();
  if (!query) {
    searchResults.value = [];
    return;
  }

  searchTimeout = setTimeout(async () => {
    try {
      isLoading.value = true;
      const response = await searchConversations(query);
      searchResults.value = response.data;
    } catch (error) {
      console.error('Search failed:', error);
      searchResults.value = [];
    } finally {
      isLoading.value = false;
    }
  }, 300);
};

const handleSelectConversation = (conversation: Conversation) => {
  if (route.params.id === conversation.id) return;

  conversationStore.setAgentFilter(null);
  emit('select-conversation', conversation);
  emit('update:modelValue', false);
  resetState();
};

const handleNewChat = (agent: Agent) => {
  emit('new-chat', agent);
  emit('update:modelValue', false);
  resetState();
};

const handleOutsideClick = () => {
  if (expanded.value) {
    expanded.value = false;
  }
};

const resetState = () => {
  searchQuery.value = '';
  searchResults.value = [];
  expanded.value = false;
};

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      loadAllConversations();
    } else {
      resetState();
    }
  },
);
</script>

<style scoped lang="css">
.v-card {
  padding: 32px;
  border-radius: 24px !important;
  background-color: #fff;
  color: black;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-title {
  color: #303030;
  font-family: Poppins;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.dialog-header .v-icon {
  color: #959595 !important;
  cursor: pointer;
}

.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
  min-height: 197px;
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 10px 16px;
  border: 1px solid #dadada;
  border-radius: 8px;
  background-color: white;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  color: #1e1e1e;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  background: transparent;
}

.search-input::placeholder {
  color: #d9d9d9;
}

.search-icon {
  color: #d9d9d9;
  flex-shrink: 0;
}

.new-chat-selector {
  border-radius: 8px;
  background-color: #f9f9f9;
  cursor: pointer;
  overflow: hidden;
}

.new-chat-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 12px 16px;
}

.new-chat-icon {
  color: #959595;
}

.new-chat-text {
  flex: 1;
  color: #959595;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.new-chat-chevron {
  color: #959595;
}

.agent-list {
  background-color: #f9f9f9;
}

.agent-item {
  padding: 12px 16px;
  cursor: pointer;
}

.agent-item:hover {
  background-color: #f0f0f0;
}

.agent-item-title {
  padding-left: 32px;
  color: #959595;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.agent-item:hover .agent-item-title {
  color: #f77d1a;
}

.conversation-groups {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.conversation-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.group-label {
  color: #959595;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
}

.group-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.history-item:hover {
  background-color: #f9f9f9;
}

.history-item--active {
  background-color: #f0f0f0;
}

.history-item-title {
  color: #1e1e1e;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  color: #959595;
  font-family: Poppins;
  font-size: 14px;
  text-align: center;
  padding: 24px 0;
}
</style>
